/**
 * Labor & Equipment Input Component
 * Reusable component for adding labor and equipment costs to calculator items
 * Supports tab-specific labor/equipment types and manual entry
 * Matches estimate functionality with workers, hrs/day, duration, rate
 */

import React, { useState } from 'react';
import { Users, HardHat, Plus, Trash2, Edit3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { defaultLaborTypes, defaultEquipmentTypes, tabSpecificLaborTypes, tabSpecificEquipmentTypes } from './LaborEquipmentRates';

const formatCurrency = (num) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num || 0);
};

const LaborEquipmentInput = ({ 
  laborItems = [], 
  setLaborItems,
  equipmentItems = [],
  setEquipmentItems,
  tabType = null, // 'demo', 'excavation', 'civil', 'environmental'
}) => {
  // Labor input state
  const [selectedLabor, setSelectedLabor] = useState('');
  const [manualLaborEntry, setManualLaborEntry] = useState(false);
  const [manualLaborName, setManualLaborName] = useState('');
  const [laborWorkers, setLaborWorkers] = useState('1');
  const [laborHoursPerDay, setLaborHoursPerDay] = useState('8');
  const [laborDuration, setLaborDuration] = useState('');
  const [laborDurationType, setLaborDurationType] = useState('days');
  const [laborRate, setLaborRate] = useState('');
  const [laborOTHours, setLaborOTHours] = useState('');
  const [laborOTRate, setLaborOTRate] = useState('');
  
  // Equipment input state
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [manualEquipmentEntry, setManualEquipmentEntry] = useState(false);
  const [manualEquipmentName, setManualEquipmentName] = useState('');
  const [equipmentQuantity, setEquipmentQuantity] = useState('1');
  const [equipmentDuration, setEquipmentDuration] = useState('');
  const [equipmentDurationType, setEquipmentDurationType] = useState('days');
  const [equipmentRate, setEquipmentRate] = useState('');
  const [fuelConsumption, setFuelConsumption] = useState(''); // gal/hr
  const [fuelCostPerGal, setFuelCostPerGal] = useState(''); // $/gal

  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Get tab-specific labor types (no rates shown in dropdown)
  const getLaborTypes = () => {
    const tabSpecific = tabType && tabSpecificLaborTypes[tabType] ? tabSpecificLaborTypes[tabType] : [];
    const generalTypes = defaultLaborTypes.filter(l => l.category === 'General' || l.category === 'Operators');
    return [...tabSpecific, ...generalTypes];
  };

  // Get tab-specific equipment types (no rates shown in dropdown)
  const getEquipmentTypes = () => {
    const tabSpecific = tabType && tabSpecificEquipmentTypes[tabType] ? tabSpecificEquipmentTypes[tabType] : [];
    const generalTypes = defaultEquipmentTypes.filter(e => e.category === 'General' || e.category === 'Trucks');
    return [...tabSpecific, ...generalTypes];
  };

  const laborTypes = getLaborTypes();
  const equipmentTypes = getEquipmentTypes();

  // Calculate labor total based on duration type (includes OT)
  const calculateLaborTotal = (workers, hoursPerDay, duration, durationType, rate, otHours, otRate) => {
    const w = parseFloat(workers) || 0;
    const hpd = parseFloat(hoursPerDay) || 8;
    const d = parseFloat(duration) || 0;
    const r = parseFloat(rate) || 0;
    const ot = parseFloat(otHours) || 0;
    const otr = parseFloat(otRate) || 0;
    
    let totalRegularHours = 0;
    switch (durationType) {
      case 'hours': totalRegularHours = d; break;
      case 'days': totalRegularHours = d * hpd; break;
      case 'weeks': totalRegularHours = d * 5 * hpd; break;
      case 'months': totalRegularHours = d * 22 * hpd; break;
      default: totalRegularHours = d * hpd;
    }
    const regularCost = w * totalRegularHours * r;
    const otCost = w * ot * otr;
    return regularCost + otCost;
  };

  // Calculate equipment total based on duration type (includes fuel)
  const calculateEquipmentTotal = (quantity, duration, durationType, rate, fuelGalHr = 0, fuelCost = 0) => {
    const q = parseFloat(quantity) || 0;
    const d = parseFloat(duration) || 0;
    const r = parseFloat(rate) || 0;
    const fg = parseFloat(fuelGalHr) || 0;
    const fc = parseFloat(fuelCost) || 0;
    
    let totalDays = 0;
    let totalHours = 0;
    switch (durationType) {
      case 'hours': totalDays = d / 8; totalHours = d; break;
      case 'days': totalDays = d; totalHours = d * 8; break;
      case 'weeks': totalDays = d * 5; totalHours = d * 5 * 8; break;
      case 'months': totalDays = d * 22; totalHours = d * 22 * 8; break;
      default: totalDays = d; totalHours = d * 8;
    }
    const equipmentCost = q * totalDays * r;
    const fuelTotalCost = q * totalHours * fg * fc;
    return { equipmentCost, fuelTotalCost, totalCost: equipmentCost + fuelTotalCost };
  };

  // Add labor item
  const addLaborItem = () => {
    const name = manualLaborEntry ? manualLaborName : selectedLabor;
    
    if (!name) {
      toast.error('Please select or enter a labor type');
      return;
    }
    if (!laborDuration || !laborRate) {
      toast.error('Please enter duration and rate');
      return;
    }

    const totalCost = calculateLaborTotal(laborWorkers, laborHoursPerDay, laborDuration, laborDurationType, laborRate, laborOTHours, laborOTRate);

    const newItem = {
      id: generateId(),
      type: 'labor',
      name: name,
      workers: parseInt(laborWorkers) || 1,
      hoursPerDay: parseInt(laborHoursPerDay) || 8,
      duration: parseFloat(laborDuration) || 0,
      durationType: laborDurationType,
      rate: parseFloat(laborRate) || 0,
      otHours: parseFloat(laborOTHours) || 0,
      otRate: parseFloat(laborOTRate) || 0,
      totalCost,
      isManual: manualLaborEntry,
      tabType: tabType,
      addedAt: new Date().toISOString()
    };

    setLaborItems([...laborItems, newItem]);
    toast.success(`${name} added`);
    
    // Reset form
    setSelectedLabor('');
    setManualLaborName('');
    setLaborWorkers('1');
    setLaborHoursPerDay('8');
    setLaborDuration('');
    setLaborDurationType('days');
    setLaborRate('');
    setLaborOTHours('');
    setLaborOTRate('');
  };

  // Add equipment item
  const addEquipmentItem = () => {
    const name = manualEquipmentEntry ? manualEquipmentName : selectedEquipment;
    
    if (!name) {
      toast.error('Please select or enter equipment type');
      return;
    }
    if (!equipmentDuration || !equipmentRate) {
      toast.error('Please enter duration and rate');
      return;
    }

    const { equipmentCost, fuelTotalCost, totalCost } = calculateEquipmentTotal(
      equipmentQuantity, equipmentDuration, equipmentDurationType, equipmentRate,
      fuelConsumption, fuelCostPerGal
    );

    const newItem = {
      id: generateId(),
      type: 'equipment',
      name: name,
      quantity: parseInt(equipmentQuantity) || 1,
      duration: parseFloat(equipmentDuration) || 0,
      durationType: equipmentDurationType,
      rate: parseFloat(equipmentRate) || 0,
      fuelConsumption: parseFloat(fuelConsumption) || 0,
      fuelCostPerGal: parseFloat(fuelCostPerGal) || 0,
      equipmentCost,
      fuelTotalCost,
      totalCost,
      isManual: manualEquipmentEntry,
      tabType: tabType,
      addedAt: new Date().toISOString()
    };

    setEquipmentItems([...equipmentItems, newItem]);
    toast.success(`${name} added`);
    
    // Reset form
    setSelectedEquipment('');
    setManualEquipmentName('');
    setEquipmentQuantity('1');
    setEquipmentDuration('');
    setEquipmentDurationType('days');
    setEquipmentRate('');
    setFuelConsumption('');
    setFuelCostPerGal('');
  };

  // Remove items
  const removeLaborItem = (id) => {
    setLaborItems(laborItems.filter(item => item.id !== id));
    toast.success('Labor item removed');
  };

  const removeEquipmentItem = (id) => {
    setEquipmentItems(equipmentItems.filter(item => item.id !== id));
    toast.success('Equipment item removed');
  };

  // Calculate totals
  const laborTotal = laborItems.reduce((sum, item) => sum + (item.totalCost || 0), 0);
  const equipmentTotal = equipmentItems.reduce((sum, item) => sum + (item.totalCost || 0), 0);
  const grandTotal = laborTotal + equipmentTotal;

  // Group labor types by category
  const groupedLaborTypes = laborTypes.reduce((acc, labor) => {
    if (!acc[labor.category]) acc[labor.category] = [];
    acc[labor.category].push(labor);
    return acc;
  }, {});

  // Group equipment types by category
  const groupedEquipmentTypes = equipmentTypes.reduce((acc, equip) => {
    if (!acc[equip.category]) acc[equip.category] = [];
    acc[equip.category].push(equip);
    return acc;
  }, {});

  // Get tab label
  const getTabLabel = () => {
    const labels = {
      demo: 'Demolition',
      excavation: 'Excavation',
      backfill: 'Backfill',
      import: 'Import',
      civil: 'Civil',
      environmental: 'Environmental'
    };
    return labels[tabType] || '';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Labor & Equipment
            {tabType && (
              <Badge variant="outline" className="ml-2 text-xs">{getTabLabel()}</Badge>
            )}
          </span>
          {grandTotal > 0 && (
            <Badge className="bg-primary">{formatCurrency(grandTotal)}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Labor Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-medium">
              <Users className="w-4 h-4 text-blue-500" />
              Labor
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="manual-labor"
                checked={manualLaborEntry}
                onCheckedChange={setManualLaborEntry}
              />
              <Label htmlFor="manual-labor" className="text-xs cursor-pointer flex items-center gap-1">
                <Edit3 className="w-3 h-3" />
                Manual Entry
              </Label>
            </div>
          </div>
          
          {/* Labor Input Row */}
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">{manualLaborEntry ? 'Labor Name' : 'Labor Type'}</Label>
              {manualLaborEntry ? (
                <Input
                  placeholder="Enter labor name"
                  value={manualLaborName}
                  onChange={(e) => setManualLaborName(e.target.value)}
                  data-testid="manual-labor-name"
                />
              ) : (
                <Select value={selectedLabor} onValueChange={setSelectedLabor}>
                  <SelectTrigger data-testid="labor-type-select">
                    <SelectValue placeholder="Select labor type" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {Object.entries(groupedLaborTypes).map(([category, items]) => (
                      <div key={category}>
                        <div className="px-2 py-1 text-xs font-semibold bg-muted sticky top-0">{category}</div>
                        {items.map(labor => (
                          <SelectItem key={labor.id} value={labor.name}>
                            {labor.name}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Workers</Label>
              <Input
                type="number"
                min="1"
                value={laborWorkers}
                onChange={(e) => setLaborWorkers(e.target.value)}
                data-testid="labor-workers"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hrs/Day</Label>
              <Select value={laborHoursPerDay} onValueChange={setLaborHoursPerDay}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4</SelectItem>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="8">8</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="12">12</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Duration</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                placeholder="0"
                value={laborDuration}
                onChange={(e) => setLaborDuration(e.target.value)}
                data-testid="labor-duration"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Unit</Label>
              <Select value={laborDurationType} onValueChange={setLaborDurationType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="weeks">Weeks</SelectItem>
                  <SelectItem value="months">Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Rate ($/hr)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={laborRate}
                onChange={(e) => setLaborRate(e.target.value)}
                data-testid="labor-rate"
              />
            </div>
          </div>
          
          {/* Overtime Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="col-span-2 flex items-center gap-2">
              <Badge variant="outline" className="text-amber-600 border-amber-500">OT</Badge>
              <span className="text-xs text-muted-foreground">Overtime (optional)</span>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">OT Hours</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                placeholder="0"
                value={laborOTHours}
                onChange={(e) => setLaborOTHours(e.target.value)}
                data-testid="labor-ot-hours"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">OT Rate ($/hr)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={laborOTRate}
                onChange={(e) => setLaborOTRate(e.target.value)}
                data-testid="labor-ot-rate"
              />
            </div>
          </div>
          <Button onClick={addLaborItem} size="sm" data-testid="add-labor-btn">
            <Plus className="w-4 h-4 mr-1" />Add Labor
          </Button>
          
          {/* Labor Items List */}
          {laborItems.length > 0 && (
            <div className="space-y-2">
              {laborItems.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      {item.name}
                      {item.isManual && <Badge variant="outline" className="text-xs">Manual</Badge>}
                      {item.otHours > 0 && <Badge variant="outline" className="text-xs text-amber-600 border-amber-500">+OT</Badge>}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {item.workers} worker{item.workers > 1 ? 's' : ''} × {item.duration} {item.durationType} × {item.hoursPerDay}hrs/day × ${item.rate}/hr
                      {item.otHours > 0 && ` + ${item.otHours}hrs OT @ $${item.otRate}/hr`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-blue-600">{formatCurrency(item.totalCost)}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeLaborItem(item.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-2">
                <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  Labor Total: {formatCurrency(laborTotal)}
                </Badge>
              </div>
            </div>
          )}
        </div>

        {/* Equipment Section */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-medium">
              <HardHat className="w-4 h-4 text-orange-500" />
              Equipment
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="manual-equipment"
                checked={manualEquipmentEntry}
                onCheckedChange={setManualEquipmentEntry}
              />
              <Label htmlFor="manual-equipment" className="text-xs cursor-pointer flex items-center gap-1">
                <Edit3 className="w-3 h-3" />
                Manual Entry
              </Label>
            </div>
          </div>
          
          {/* Equipment Input Row */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">{manualEquipmentEntry ? 'Equipment Name' : 'Equipment Type'}</Label>
              {manualEquipmentEntry ? (
                <Input
                  placeholder="Enter equipment name"
                  value={manualEquipmentName}
                  onChange={(e) => setManualEquipmentName(e.target.value)}
                  data-testid="manual-equipment-name"
                />
              ) : (
                <Select value={selectedEquipment} onValueChange={setSelectedEquipment}>
                  <SelectTrigger data-testid="equipment-type-select">
                    <SelectValue placeholder="Select equipment" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {Object.entries(groupedEquipmentTypes).map(([category, items]) => (
                      <div key={category}>
                        <div className="px-2 py-1 text-xs font-semibold bg-muted sticky top-0">{category}</div>
                        {items.map(equip => (
                          <SelectItem key={equip.id} value={equip.name}>
                            {equip.name}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Quantity</Label>
              <Input
                type="number"
                min="1"
                value={equipmentQuantity}
                onChange={(e) => setEquipmentQuantity(e.target.value)}
                data-testid="equipment-quantity"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Duration</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                placeholder="0"
                value={equipmentDuration}
                onChange={(e) => setEquipmentDuration(e.target.value)}
                data-testid="equipment-duration"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Unit</Label>
              <Select value={equipmentDurationType} onValueChange={setEquipmentDurationType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="weeks">Weeks</SelectItem>
                  <SelectItem value="months">Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Rate ($/day)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={equipmentRate}
                onChange={(e) => setEquipmentRate(e.target.value)}
                data-testid="equipment-rate"
              />
            </div>
          </div>
          
          {/* Fuel Row */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Fuel Consumption (gal/hr)</Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                placeholder="0"
                value={fuelConsumption}
                onChange={(e) => setFuelConsumption(e.target.value)}
                data-testid="fuel-consumption"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Fuel Cost ($/gal)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={fuelCostPerGal}
                onChange={(e) => setFuelCostPerGal(e.target.value)}
                data-testid="fuel-cost"
              />
            </div>
          </div>
          
          <Button onClick={addEquipmentItem} size="sm" data-testid="add-equipment-btn">
            <Plus className="w-4 h-4 mr-1" />Add Equipment
          </Button>
          
          {/* Equipment Items List */}
          {equipmentItems.length > 0 && (
            <div className="space-y-2">
              {equipmentItems.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      {item.name}
                      {item.isManual && <Badge variant="outline" className="text-xs">Manual</Badge>}
                      {item.fuelTotalCost > 0 && <Badge variant="outline" className="text-xs text-green-600 border-green-500">+Fuel</Badge>}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {item.quantity} unit{item.quantity > 1 ? 's' : ''} × {item.duration} {item.durationType} × ${item.rate}/day
                      {item.fuelTotalCost > 0 && ` + ${item.fuelConsumption}gal/hr @ $${item.fuelCostPerGal}/gal`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="font-bold text-orange-600">{formatCurrency(item.totalCost)}</span>
                      {item.fuelTotalCost > 0 && (
                        <p className="text-xs text-green-600">Fuel: {formatCurrency(item.fuelTotalCost)}</p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeEquipmentItem(item.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-2">
                <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                  Equipment Total: {formatCurrency(equipmentTotal)}
                </Badge>
              </div>
            </div>
          )}
        </div>

        {/* Grand Total */}
        {grandTotal > 0 && (
          <div className="p-4 bg-primary/10 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total Labor & Equipment</span>
              <span className="text-xl font-bold text-primary">{formatCurrency(grandTotal)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LaborEquipmentInput;
